import { h, setChildren } from "../core/dom.js";
import { api, setToken } from "../api.js";
import { profile, loadProfile, loadMuscles, refreshActiveSession, refreshPendingRequests, clearUserSession } from "../store.js";
import { navigate } from "../core/router.js";

// New accounts (not yet through first-run setup) go to /welcome; everyone else home.
function goAfterAuth() {
  navigate(profile()?.onboarded ? "/" : "/welcome", { replace: true });
}
import { icon } from "../lib/icons.js";
import { toast } from "../components/ui.js";
import { legalLinks } from "./Legal.js";

// Brand glyphs for the OAuth buttons (kept inline so we don't add image assets).
const OAUTH_PROVIDERS = {
  google: {
    label: "Google",
    svg: `<svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.6l-6.6 5.1C9.6 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.4 36.9 44 31 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>`,
  },
  github: {
    label: "GitHub",
    svg: `<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 .5C5.7.5.5 5.7.5 12c0 5.1 3.3 9.4 7.9 10.9.6.1.8-.2.8-.6v-2c-3.2.7-3.9-1.4-3.9-1.4-.5-1.3-1.3-1.7-1.3-1.7-1.1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1 1.8 2.7 1.3 3.4 1 .1-.8.4-1.3.7-1.6-2.6-.3-5.3-1.3-5.3-5.7 0-1.3.4-2.3 1.2-3.1-.1-.3-.5-1.5.1-3.1 0 0 1-.3 3.3 1.2a11.4 11.4 0 0 1 6 0C17.3 5 18.3 5.3 18.3 5.3c.6 1.6.2 2.8.1 3.1.8.8 1.2 1.8 1.2 3.1 0 4.4-2.7 5.4-5.3 5.7.4.4.8 1.1.8 2.2v3.3c0 .4.2.7.8.6 4.6-1.5 7.9-5.8 7.9-10.9C23.5 5.7 18.3.5 12 .5z"/></svg>`,
  },
};

// Lazily load Cloudflare Turnstile's widget script (explicit-render mode) exactly once.
// Resolves with window.turnstile when ready; rejects if it can't load.
let turnstilePromise = null;
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstilePromise) return turnstilePromise;
  turnstilePromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.turnstile);
    s.onerror = () => reject(new Error("Turnstile failed to load"));
    document.head.appendChild(s);
  });
  return turnstilePromise;
}

export default function AuthScreen() {
  const root = h("div", { class: "auth" });
  let mode = "login"; // or "register"
  let captcha = null;
  let registrationEnabled = true;
  let oauthProviders = [];
  let turnstileSiteKey = null; // set from /api/config when Turnstile is enabled server-side
  let turnstileToken = null; // current widget response token (single-use)
  let turnstileWidgetId = null;
  let inviteCode = null; // invite code (from an invite link or typed in), for invite-only signup
  let inviteMode = false; // registering via invite while public registration is closed

  // Handle the redirect back from an OAuth provider (token/error in the URL fragment).
  consumeOAuthRedirect();
  // An invite link (kashtu.app/login#invite=CODE) prefills the code and opens the register form
  // even when public registration is closed.
  consumeInvite();

  api.config().then((c) => {
    registrationEnabled = c.registration_enabled !== false;
    oauthProviders = Array.isArray(c.oauth_providers) ? c.oauth_providers : [];
    turnstileSiteKey = c.turnstile_site_key || null;
    // Don't kick an invited user out of the register form when registration is closed.
    if (!registrationEnabled && mode === "register" && !inviteMode) mode = "login";
    rerender();
  }).catch(() => {});

  function consumeInvite() {
    const params = new URLSearchParams(location.hash.replace(/^#/, ""));
    const code = params.get("invite");
    if (!code) return;
    inviteCode = code.trim();
    inviteMode = true;
    mode = "register";
    history.replaceState({}, "", location.pathname); // strip the code from the address bar
  }

  async function consumeOAuthRedirect() {
    const hash = location.hash.replace(/^#/, "");
    if (!hash) return;
    const params = new URLSearchParams(hash);
    const token = params.get("token");
    const error = params.get("error");
    const verified = params.get("verified");
    if (!token && !error && !verified) return;
    history.replaceState({}, "", location.pathname); // strip the fragment
    if (error) { toast(error, "error"); return; }
    if (verified) { toast("Email confirmed — thanks!", "success"); return; }
    try {
      clearUserSession();
      setToken(token);
      await Promise.all([loadProfile(), loadMuscles()]);
      await refreshActiveSession();
      refreshPendingRequests();
      goAfterAuth();
    } catch {
      clearUserSession();
      toast("Sign-in failed — please try again", "error");
    }
  }

  async function refreshCaptcha() {
    try {
      captcha = await api.captcha();
      const q = root.querySelector("#captcha-q");
      if (q) q.textContent = captcha.question;
    } catch {}
  }

  function rerender() {
    setChildren(root, view());
    // Bot check only on the open public signup — invited signups are vouched for by the code.
    if (mode === "register" && registrationEnabled) {
      if (turnstileSiteKey) mountTurnstile();
      else refreshCaptcha();
    }
  }

  // Render (or re-render) the Turnstile widget into the placeholder, refreshing the token.
  function mountTurnstile() {
    const el = root.querySelector("#turnstile-container");
    if (!el) return;
    turnstileToken = null;
    loadTurnstile()
      .then((ts) => {
        if (!root.querySelector("#turnstile-container")) return; // navigated away
        el.innerHTML = "";
        turnstileWidgetId = ts.render(el, {
          sitekey: turnstileSiteKey,
          callback: (token) => { turnstileToken = token; },
          "error-callback": () => { turnstileToken = null; },
          "expired-callback": () => { turnstileToken = null; },
        });
      })
      .catch(() => {});
  }

  function resetTurnstile() {
    turnstileToken = null;
    try { if (turnstileWidgetId != null && window.turnstile) window.turnstile.reset(turnstileWidgetId); } catch {}
  }

  async function submit(e) {
    e.preventDefault();
    const f = new FormData(e.target);
    const btn = e.target.querySelector("button[type=submit]");

    // Forgot-password: ask the server to email a reset link. The response is deliberately
    // the same whether or not the address is registered, so we just confirm and return.
    if (mode === "forgot") {
      btn.disabled = true;
      try {
        await api.forgotPassword(f.get("email")?.trim());
        toast("If that email is registered, a reset link is on its way.", "success");
        mode = "login";
        rerender();
      } catch (err) {
        toast(err.message || "Failed", "error");
        btn.disabled = false;
      }
      return;
    }

    const username = f.get("username")?.trim();
    const password = f.get("password");
    btn.disabled = true;
    try {
      let res;
      if (mode === "register") {
        if (!f.get("agree")) {
          toast("Please confirm you’re at least 16 and accept the Terms.", "error");
          btn.disabled = false;
          return;
        }
        const code = (f.get("invite_code") || inviteCode || "").trim();
        if (!registrationEnabled && !code) {
          toast("An invite code is required.", "error");
          btn.disabled = false;
          return;
        }
        // The bot check only applies to the open public flow; invited signups skip it.
        if (registrationEnabled && turnstileSiteKey && !turnstileToken) {
          toast("Please complete the bot check.", "error");
          btn.disabled = false;
          return;
        }
        res = await api.register({
          username,
          password,
          email: f.get("email")?.trim(),
          captchaToken: captcha?.token,
          captchaAnswer: Number(f.get("captcha")),
          turnstileToken: turnstileToken || undefined,
          invite_code: code || undefined,
        });
      } else {
        res = await api.login({ username, password });
      }
      clearUserSession();
      setToken(res.token);
      await Promise.all([loadProfile(), loadMuscles()]);
      await refreshActiveSession();
      goAfterAuth();
    } catch (err) {
      toast(err.message || "Failed", "error");
      btn.disabled = false;
      if (mode === "register") {
        if (turnstileSiteKey) resetTurnstile();
        else refreshCaptcha();
      }
    }
  }

  function view() {
    if (mode === "forgot") return forgotView();
    return h(
      "div",
      null,
      h(
        "div",
        { class: "auth-logo" },
        h("div", { class: "mark" }, icon("bow", { size: 34 })),
        h("h1", null, "Kashtu"),
        h("p", null, mode === "login" ? "Welcome back — let's train." : "Create your account")
      ),
      h(
        "form",
        { class: "card", onSubmit: submit },
        mode === "register" && !registrationEnabled
          ? h(
              "div",
              { class: "field" },
              h("label", null, "Invite code"),
              h("input", {
                class: "input", name: "invite_code", value: inviteCode || "", required: true,
                autocomplete: "off", autocapitalize: "characters", spellcheck: false, placeholder: "XXXX-XXXX",
              })
            )
          : null,
        h("div", { class: "field" }, h("label", null, "Username"), h("input", { class: "input", name: "username", autocomplete: "username", required: true, minlength: 3 })),
        h("div", { class: "field" }, h("label", null, "Password"), h("input", { class: "input", name: "password", type: "password", autocomplete: mode === "login" ? "current-password" : "new-password", required: true, minlength: 5 })),
        mode === "register" &&
          h(
            "div",
            { class: "field" },
            h("label", null, "Email ", h("span", { class: "dim" }, "(we'll send a confirmation link)")),
            h("input", { class: "input", name: "email", type: "email", autocomplete: "email", required: true, placeholder: "you@example.com" })
          ),
        mode === "register" && registrationEnabled && turnstileSiteKey
          ? h("div", { class: "field" }, h("div", { id: "turnstile-container", class: "turnstile" }))
          : mode === "register" && registrationEnabled
          ? h(
              "div",
              { class: "field" },
              h("label", null, h("span", { id: "captcha-q" }, captcha?.question || "Loading…"), " (spam check)"),
              h("input", { class: "input", name: "captcha", type: "number", inputmode: "numeric", required: true, placeholder: "Answer" })
            )
          : null,
        mode === "register"
          ? h(
              "label",
              { class: "auth-agree" },
              h("input", { type: "checkbox", name: "agree", value: "1", required: true }),
              h(
                "span",
                null,
                "I’m at least 16 years old and agree to the ",
                h("a", { href: "/terms", "data-link": "/terms" }, "Terms"),
                " and ",
                h("a", { href: "/privacy", "data-link": "/privacy" }, "Privacy Policy"),
                "."
              )
            )
          : null,
        h("button", { class: "btn btn-primary btn-block btn-lg", type: "submit" }, mode === "login" ? "Log in" : "Create account")
      ),
      mode === "login"
        ? h("div", { class: "auth-switch" }, h("button", { onClick: () => { mode = "forgot"; rerender(); } }, "Forgot password?"))
        : null,
      // OAuth signup is blocked while closed, so hide the buttons in the invite-register flow
      // (they still show on the login screen for existing users to sign in).
      oauthProviders.length && !(mode === "register" && !registrationEnabled) ? oauthSection() : null,
      registrationEnabled
        ? h(
            "div",
            { class: "auth-switch" },
            mode === "login" ? "New here? " : "Already have an account? ",
            h(
              "button",
              {
                onClick: () => {
                  mode = mode === "login" ? "register" : "login";
                  rerender();
                },
              },
              mode === "login" ? "Create an account" : "Log in"
            )
          )
        : mode === "register"
        ? h(
            "div",
            { class: "auth-switch" },
            h("button", { onClick: () => { mode = "login"; inviteMode = false; inviteCode = null; rerender(); } }, "Back to log in")
          )
        : h(
            "div",
            { class: "auth-switch dim" },
            "Kashtu is invite-only right now. ",
            h("button", { onClick: () => { inviteMode = true; mode = "register"; rerender(); } }, "Have an invite code?")
          ),
      legalLinks()
    );
  }

  function forgotView() {
    return h(
      "div",
      null,
      h(
        "div",
        { class: "auth-logo" },
        h("div", { class: "mark" }, icon("bow", { size: 34 })),
        h("h1", null, "Reset password"),
        h("p", null, "Enter your email and we'll send a reset link.")
      ),
      h(
        "form",
        { class: "card", onSubmit: submit },
        h("div", { class: "field" }, h("label", null, "Email"), h("input", { class: "input", name: "email", type: "email", autocomplete: "email", required: true, placeholder: "you@example.com" })),
        h("button", { class: "btn btn-primary btn-block btn-lg", type: "submit" }, "Send reset link")
      ),
      h("div", { class: "auth-switch" }, h("button", { onClick: () => { mode = "login"; rerender(); } }, "Back to log in"))
    );
  }

  function oauthSection() {
    return h("div", { class: "oauth" },
      h("div", { class: "oauth-divider" }, h("span", null, "or")),
      ...oauthProviders
        .filter((p) => OAUTH_PROVIDERS[p])
        .map((p) => {
          const cfg = OAUTH_PROVIDERS[p];
          return h("button", {
            type: "button",
            class: "btn btn-ghost btn-block oauth-btn",
            onClick: () => { location.href = `/api/auth/oauth/${p}`; },
          },
            h("span", { class: "oauth-ico", html: cfg.svg }),
            `Continue with ${cfg.label}`
          );
        })
    );
  }

  rerender();
  return root;
}
